import gql from "graphql-tag";

import type {ApolloGqlClient} from "@khan/data-gql-apollo";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";

import {fetchProfile} from "#data-access/user-profile-query.ts";
import ProfileSingleton, {
    type Profile as ProfileType,
    type ViewPermissions,
} from "#profile-nav-package/profile.tsx";
import type {ProfileModelType} from "#shared-package/profile-model.ts";

export type Profile = ProfileType;
export const profileLessFiles = (): Array<Promise<unknown>> => [
    import("../../../stylesheets/shared-package/shared.less"),
    import("../../../stylesheets/grids-package/grids.less"),
    import("../../../stylesheets/mobile-package/mobile.less"),
    import("../../../stylesheets/profile-package/profile.less"),
];

const identifiableField = (kaidOrUsername: string): "kaid" | "username" => {
    return kaidOrUsername.startsWith("kaid_") ? "kaid" : "username";
};

type Data = {
    user?: {
        id: string;
        isCoachListWritable: boolean;
        isManagedByActor: boolean;
    };
};
type Variables = {
    kaid?: string | null | undefined;
    username?: string | null | undefined;
};
export const PROFILE_PERMISSIONS_QUERY: GqlOperation<Data, Variables> = gqlOp(
    gql`
        query profilePermissions($kaid: String, $username: String) {
            user(kaid: $kaid, username: $username) {
                id
                isCoachListWritable
                isManagedByActor
            }
        }
    `,
);

// Check whether the current user is looking at the profile of one of their
// managed coachees or if the current user has permission to write to the
// profile's coach/teacher list.
const fetchPermissions = (
    gqlClient: ApolloGqlClient,
    kaidOrUsername: string,
): Promise<ViewPermissions> => {
    const variableField = identifiableField(kaidOrUsername);
    const variables = {
        [variableField as string]: kaidOrUsername,
    } as const;

    return gqlClient
        .query(PROFILE_PERMISSIONS_QUERY, {
            variables,
            fetchPolicy: "network-only",
        })
        .then(({data}) => {
            const user = data?.user;
            return {
                isCoachListWritable: !!user?.isCoachListWritable,
                isManagedByActor: !!user?.isManagedByActor,
            };
        });
};

const _profilePromises: Record<
    string,
    Promise<[ProfileModelType, ViewPermissions]>
> = {};

// Initialize the profile:
// 1. Fetch the profile
// 2. Fetch the permissions
// 3. Initialize the Profile view (profile.js) with the data
// 4. Return the initialized ProfileSingleton
const initializeProfile = (
    gqlClient: ApolloGqlClient,
    kaidOrUsername: string,
): Promise<Profile> => {
    if (!ProfileUtil._profilePromises[kaidOrUsername]) {
        // Wait for both fetches to succeed.
        ProfileUtil._profilePromises[kaidOrUsername] = Promise.all([
            getProfileModel(gqlClient, kaidOrUsername),
            fetchPermissions(gqlClient, kaidOrUsername),
        ]);
    }

    return ProfileUtil._profilePromises[kaidOrUsername].then(
        ([profileModel, viewPermissions]: [any, any]) => {
            // Initialize the profile singleton
            ProfileSingleton.init({
                profile: profileModel,
                viewPermissions,
            });

            return ProfileSingleton;
        },
    );
};

// The default profile is the profile for the current user.  We want
// to include this profile as one of the possible profiles to search for
let defaultProfilePromise: Promise<ProfileModelType> | null | undefined =
    undefined;
const _profileModelPromises: Array<Promise<ProfileModelType>> = [];

const initializeDefaultProfile = (profileModel: ProfileModelType) => {
    if (!defaultProfilePromise) {
        defaultProfilePromise = Promise.resolve(profileModel);
        ProfileUtil._profileModelPromises.push(defaultProfilePromise);
    }
};

const _findExistingProfile = (
    kaidOrUsername: string,
): Promise<ProfileModelType | null | undefined> => {
    // When all the existing promises come back, we can identify existing
    // profiles by either "kaid" or "username"
    const variableField = identifiableField(kaidOrUsername);

    if (kaidIsDefault(kaidOrUsername) && defaultProfilePromise) {
        return defaultProfilePromise;
    }

    return Promise.all(ProfileUtil._profileModelPromises).then(
        (profileModels: Array<ProfileModelType>) => {
            return profileModels.find(
                (profileModel) =>
                    profileModel &&
                    // @ts-expect-error - TS2769 - No overload matches this call.
                    profileModel.get(variableField) === kaidOrUsername,
            );
        },
    );
};

const getProfileModel = (
    gqlClient: ApolloGqlClient,
    kaidOrUsername: string,
): Promise<ProfileModelType> => {
    return ProfileUtil._findExistingProfile(kaidOrUsername).then(
        (existingProfile) => {
            if (existingProfile) {
                return existingProfile;
            }

            const field = identifiableField(kaidOrUsername);
            const variables =
                kaidOrUsername !== ""
                    ? {[field as string]: kaidOrUsername}
                    : {};

            const profilePromise = fetchProfile(gqlClient, variables);
            ProfileUtil._profileModelPromises.push(profilePromise);

            return profilePromise;
        },
    );
};

const getProfilePathFromLoggedInUser = (
    profileModel?: ProfileModelType | null,
): string => {
    if (profileModel) {
        return profileModel.get("profileRoot");
    }

    return "/profile/";
};

const getKaidOrUsernameFromPath = (pathname: string): undefined | string => {
    if (pathname.startsWith("/profile/")) {
        const segments = pathname.split("/");
        return segments[2];
    }

    return undefined;
};

const getProfileRootPath = (
    profileModel: ProfileModelType | null | undefined,
    location: {
        readonly pathname: string;
    },
): string => {
    const kaid = getKaidOrUsernameFromPath(location.pathname);
    return kaid ? `/profile/${kaid}/` : "/profile/me/";
};

const profilePathIsMine = (
    profileModel: ProfileModelType | null | undefined,
    location: {
        readonly pathname: string;
    },
): boolean => {
    const profileIdentifier = getKaidOrUsernameFromPath(location.pathname);

    // If we don't have a KAID or username in the LIHP path, we assume it's
    // the user's profile (since we will be rendering theirs).
    // NOTE(angelo): This logic was maintained from a previous
    //  implementation that may not have intended this behavior, but this
    //  is what it did. The way our LIHP code works this allows loading
    //  of `/` (which is what happens after login) to default to class
    //  progress instead of general progress.
    if (!profileIdentifier) {
        return true;
    }

    if (profileIdentifier === "me") {
        return true;
    }

    if (profileModel) {
        // KAID based profile path
        if (profileModel.get("kaid") === profileIdentifier) {
            return true;
        }

        // Username based profile path
        if (profileModel.get("username") === profileIdentifier) {
            return true;
        }

        return false;
    }

    return "/profile/" === location.pathname;
};

const kaidIsDefault = (kaid?: string | null): boolean => {
    return !kaid || kaid === "me" || kaid === "nouser";
};

const KAID_REGEX = /^kaid_\d+$/;

const isKaidValid = (kaid?: string | null): boolean => {
    if (!kaid) {
        return false;
    }

    return KAID_REGEX.test(kaid);
};

const ProfileUtil = {
    getKaidOrUsernameFromPath,
    getProfileModel,
    getProfilePathFromLoggedInUser,
    getProfileRootPath,
    initializeDefaultProfile,
    initializeProfile,
    isKaidValid,
    kaidIsDefault,
    profilePathIsMine,
    _findExistingProfile, // exported for testing purposes
    _profilePromises, // exported for testing purposes
    _profileModelPromises, // exported for testing purposes
} as const;

// The default export is used for testing.
export default ProfileUtil;

export {
    getKaidOrUsernameFromPath,
    getProfileModel,
    getProfilePathFromLoggedInUser,
    getProfileRootPath,
    initializeDefaultProfile,
    initializeProfile,
    isKaidValid,
    kaidIsDefault,
    profilePathIsMine,
};
